// Diary record codec (I-1a Batch 3). The REAL kind and the store-level links sit
// inside the ciphertext (decision 1: one generic clear kind). The inner payload
// is JSON: { k: kind, d: data, l?: links }.

import {
  type Bytes,
  DATA_KEY_ID,
  fromUtf8,
  isRecordId,
  openRecord,
  RecordOpenError,
  sealRecord,
  type SealedRecord,
  utf8,
} from '../crypto'

/** Store-level links, used for "delete one moment" (deletion with its children). */
export interface RecordLinks {
  /** The moment this record belongs to (answers, activity events, a draft). */
  readonly moment?: string
  /** Photo blobs owned by this record. */
  readonly blobs?: readonly string[]
}

export interface DiaryRecord {
  readonly id: string
  /** Real kind (moment, answer, draft, word, contact, settings, activity, …). Never in clear. */
  readonly kind: string
  readonly data: unknown
  readonly links?: RecordLinks
}

interface Payload {
  k: string
  d: unknown
  l?: RecordLinks
}

/** Shape check for store-level links (also used when a backup file is opened). */
export function checkLinks(links: unknown): links is RecordLinks {
  if (typeof links !== 'object' || links === null) {
    return false
  }
  const { moment, blobs } = links as { moment?: unknown; blobs?: unknown }
  if (moment !== undefined && !isRecordId(moment)) {
    return false
  }
  if (blobs !== undefined && !(Array.isArray(blobs) && blobs.every(isRecordId))) {
    return false
  }
  return true
}

export async function encodeRecord(dataKey: CryptoKey, record: DiaryRecord, schemaVersion: number): Promise<SealedRecord> {
  if (record.kind.length === 0) {
    throw new Error('record kind must not be empty')
  }
  if (record.links !== undefined && !checkLinks(record.links)) {
    throw new Error('malformed record links')
  }
  const payload: Payload = { k: record.kind, d: record.data }
  if (record.links !== undefined) {
    payload.l = record.links
  }
  const plaintext = utf8(JSON.stringify(payload))
  try {
    return await sealRecord(dataKey, { id: record.id, kind: 'record', schemaVersion, keyId: DATA_KEY_ID }, plaintext)
  } finally {
    plaintext.fill(0)
  }
}

export async function decodeRecord(dataKey: CryptoKey, sealed: SealedRecord): Promise<DiaryRecord> {
  if (sealed.kind !== 'record' || sealed.keyId !== DATA_KEY_ID) {
    throw new RecordOpenError('not a data-key record')
  }
  const plaintext: Bytes = await openRecord(dataKey, sealed)
  let payload: unknown
  try {
    payload = JSON.parse(fromUtf8(plaintext))
  } catch {
    throw new RecordOpenError('record payload is not valid JSON')
  } finally {
    plaintext.fill(0)
  }
  if (typeof payload !== 'object' || payload === null) {
    throw new RecordOpenError('record payload has the wrong shape')
  }
  const { k, d, l } = payload as { k?: unknown; d?: unknown; l?: unknown }
  if (typeof k !== 'string' || k.length === 0 || (l !== undefined && !checkLinks(l))) {
    throw new RecordOpenError('record payload has the wrong shape')
  }
  return l === undefined ? { id: sealed.id, kind: k, data: d } : { id: sealed.id, kind: k, data: d, links: l }
}
